import type { RecordStatus } from '@dyeatlas/shared'
import { useQuery } from '@tanstack/react-query'
import {
  CheckCircle2,
  ClipboardCheck,
  FileText,
  Globe,
  RotateCcw,
  Trash2,
} from 'lucide-react'
import { NavLink, Navigate, Route, Routes } from 'react-router-dom'
import { SectionTabs } from '@/components/SectionTabs'
import { RecordBucket } from './RecordBucket'
import { ReviewQueue } from './ReviewQueue'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { api } from '@/lib/api'
import { cn } from '@/lib/utils'

interface Summary {
  draft: number
  finished: number
  in_review: number
  published: number
  rejected: number
  trashed: number
  pendingReviews: number
}

/** The buckets correspond exactly to the record statuses a researcher can act on. */
const BUCKETS: {
  slug: string
  status: RecordStatus
  label: string
  icon: typeof FileText
  description: string
}[] = [
  {
    slug: 'drafts',
    status: 'draft',
    label: 'Drafts',
    icon: FileText,
    description: 'Records still being written. Only you can see these.',
  },
  {
    slug: 'finished',
    status: 'finished',
    label: 'Finished',
    icon: CheckCircle2,
    description: 'Complete records, ready to submit for peer review.',
  },
  {
    slug: 'in-review',
    status: 'in_review',
    label: 'Awaiting approval',
    icon: ClipboardCheck,
    description: 'Submitted to the Public Database and waiting on reviewers.',
  },
  {
    slug: 'published',
    status: 'published',
    label: 'Published',
    icon: Globe,
    description: 'Approved by a super user or admin and discoverable in the Public Database.',
  },
  {
    slug: 'returned',
    status: 'rejected',
    label: 'Returned',
    icon: RotateCcw,
    description: 'A reviewer asked for changes. Edit, finish and resubmit.',
  },
  {
    slug: 'trash',
    status: 'trashed',
    label: 'Trash',
    icon: Trash2,
    description: 'Removed records. Restore them or delete them for good.',
  },
]

export function MyWorkspace() {
  const { isCurator } = useCurrentUser()
  const { data: summary } = useQuery({
    queryKey: ['workspace', 'summary'],
    queryFn: () => api.get<Summary>('/workspace/summary'),
  })

  return (
    <>
      <SectionTabs />

    <div className="flex gap-7">
      <aside className="w-56 shrink-0">
        <h1 className="font-display text-xl font-semibold text-ink">My Workspace</h1>
        <p className="mt-1 text-[13px] text-ink-muted">Your records, by where they are.</p>

        <nav className="mt-5 space-y-0.5">
          {BUCKETS.map((bucket) => (
            <SideLink
              key={bucket.slug}
              to={`/workspace/${bucket.slug}`}
              icon={<bucket.icon className="size-4" />}
              count={summary?.[bucket.status]}
            >
              {bucket.label}
            </SideLink>
          ))}
        </nav>

        {isCurator && (
          <>
            <div className="my-5 h-px bg-line" />

            <SideLink
              to="/workspace/review"
              icon={<ClipboardCheck className="size-4" />}
              count={summary?.pendingReviews}
              highlight={Boolean(summary?.pendingReviews)}
            >
              Review queue
            </SideLink>
          </>
        )}
      </aside>

      <section className="min-w-0 flex-1">
        <Routes>
          <Route index element={<Navigate to="/workspace/drafts" replace />} />
          {BUCKETS.map((bucket) => (
            <Route
              key={bucket.slug}
              path={bucket.slug}
              element={
                <RecordBucket
                  status={bucket.status}
                  title={bucket.label}
                  description={bucket.description}
                />
              }
            />
          ))}
          {isCurator && <Route path="review" element={<ReviewQueue />} />}
          <Route path="*" element={<Navigate to="/workspace/drafts" replace />} />
        </Routes>
      </section>
    </div>
    </>
  )
}

function SideLink({
  to,
  icon,
  count,
  highlight,
  children,
}: {
  to: string
  icon: React.ReactNode
  count?: number
  highlight?: boolean
  children: React.ReactNode
}) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        cn(
          'flex items-center justify-between gap-2 rounded-[var(--radius-control)] px-3 py-2 text-[13px] transition-colors',
          isActive
            ? 'bg-brand-soft font-semibold text-brand'
            : 'text-ink-soft hover:bg-paper-muted hover:text-ink',
        )
      }
    >
      <span className="flex items-center gap-2">
        {icon}
        {children}
      </span>
      {count !== undefined && count > 0 && (
        <span
          className={cn(
            'rounded-full px-1.5 text-[10px] font-semibold',
            highlight ? 'bg-brand text-white' : 'text-ink-faint',
          )}
        >
          {count}
        </span>
      )}
    </NavLink>
  )
}
